import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { queueMachineService } from '@/services/queue_machine_service'
import { useAuth } from '@/hooks/useAuth'
import { useExaminationRooms } from '@/hooks/useExaminationRooms'
import { toast } from 'sonner'
import { QueueMachineWithRelations } from '@magnetar/admin-api-interfaces'
import { Checkbox } from '@/components/ui/checkbox'
import { useEffect } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface QueueMachineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: number
  queueMachine?: QueueMachineWithRelations
  onSuccess?: () => void
}

export function QueueMachineDialog({
  open,
  onOpenChange,
  clinicId,
  queueMachine,
  onSuccess,
}: QueueMachineDialogProps) {
  const { token } = useAuth()
  const isEditing = !!queueMachine

  const { examinationRooms } = useExaminationRooms(
    clinicId !== 0
      ? {
          page: 1,
          pageSize: 100, // 假設 100 個診間足夠多
          clinic_id: clinicId,
        }
      : undefined,
  )

  const form = useForm<{
    name: string
    channel: 'DEFAULT' | 'INTERNAL' | 'THIRD_PARTY'
    show_patient_name: boolean
    examination_room_id: number | null
  }>({
    defaultValues: {
      name: '',
      channel: 'DEFAULT',
      show_patient_name: true,
      examination_room_id: null,
    },
  })

  useEffect(() => {
    if (queueMachine) {
      form.reset({
        name: queueMachine.name,
        channel: queueMachine.channel,
        show_patient_name: queueMachine.show_patient_name,
        examination_room_id: queueMachine.examination_room_id,
      })
    } else {
      form.reset({
        name: '',
        channel: 'DEFAULT',
        show_patient_name: true,
        examination_room_id: null,
      })
    }
  }, [queueMachine, form, open])

  const onSubmit = async (formValues: {
    name: string
    channel: 'DEFAULT' | 'INTERNAL' | 'THIRD_PARTY'
    show_patient_name: boolean
    examination_room_id: number | null
  }) => {
    if (!token) return

    let result

    // Create a properly typed object that matches CreateQueueMachineData
    const queueMachineData = {
      name: formValues.name,
      channel: formValues.channel,
      clinic_id: clinicId,
      show_patient_name: formValues.show_patient_name,
      examination_room_id: formValues.examination_room_id ?? null,
    }

    if (isEditing && queueMachine) {
      result = await queueMachineService.updateQueueMachine(
        queueMachine.queue_machine_id,
        formValues,
        token,
      )
    } else {
      result = await queueMachineService.createQueueMachine(queueMachineData, token)
    }

    if (result.success) {
      toast.success(isEditing ? '更新叫號機成功' : '新增叫號機成功')
      onOpenChange(false)
      onSuccess?.()
    } else {
      toast.error(isEditing ? '更新叫號機失敗' : '新增叫號機失敗', {
        description: result.error?.message,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? '編輯叫號機' : '新增叫號機'}</DialogTitle>
          <DialogDescription>{isEditing ? '更新叫號機資訊' : '填寫叫號機資訊'}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>叫號機名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入叫號機名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="channel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>頻道</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="選擇頻道" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="DEFAULT">預設</SelectItem>
                      <SelectItem value="INTERNAL">內部</SelectItem>
                      <SelectItem value="THIRD_PARTY">第三方轉接</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="examination_room_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>關聯診間</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === 'null' ? null : Number(value))
                    }
                    value={field.value === null ? 'null' : String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="請選擇診間" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="null">請選擇診間</SelectItem>
                      {examinationRooms?.map((room) => (
                        <SelectItem
                          key={room.examination_room_id}
                          value={String(room.examination_room_id)}
                        >
                          {room.name} ({room.department})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="show_patient_name"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>顯示病患名稱</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="submit">{isEditing ? '更新' : '新增'}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
